import { useState } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import AlertModal from "components/AlertModal";
import * as C from "components/EntityCard/styles";
import ModalComponent from "components/ModalComponent";
import { usePatient } from "providers/Patient";
import { api } from "services";
import { Patient } from "types/patient";
import { cpfAndCnpjMask, mobilePhoneMask } from "utils/formMasks";
import { toast } from "utils/toast";

import PatientEditForm from "../Forms/PatientEditForm";

interface PatientCardProps {
  patient: Patient;
  companyId: number;
  isActive?: boolean;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const PatientCard = ({
  patient,
  companyId,
  isActive = false,
}: PatientCardProps) => {
  const [alertModalShow, setAlertModalShow] = useState(false);
  const [patientEditModalShow, setPatientEditModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    setInactivePatientsCurrentPage,
    setActivePatientsCurrentPage,
    getInactivePatients,
    getActivePatients,
  } = usePatient();

  const handleActivatePatient = async () => {
    setIsLoading(true);
    try {
      await api.patch(`/Patient/${patient.id}/${isActive ? "disable" : "enable"}`);
      setIsLoading(false);
      toast.fire({ icon: "success", title: `Paciente ${isActive ? "des" : ""}ativado com sucesso!` });
      setInactivePatientsCurrentPage(1);
      setActivePatientsCurrentPage(1);
      getInactivePatients(companyId);
      getActivePatients(companyId);
      setAlertModalShow(false);
    } catch {
      setIsLoading(false);
      toast.fire({ icon: "error", title: "Ocorreu um erro, tente novamente." });
    }
  };

  const patientName = patient.socialName
    ? `${patient.socialName} (${patient.name})`
    : patient.name;

  const phone = mobilePhoneMask(`${patient.dddMobilePhone}${patient.mobilePhone}`);

  const address = patient.addressCEP
    ? `${patient.addressRoad}, ${patient.addressNumber}, ${patient.addressCity} - ${patient.addressState}`
    : null;

  return (
    <>
      <C.Card
        style={{ "--left-color": isActive ? "var(--primary)" : "var(--gray)" } as React.CSSProperties}
        aria-label={`Paciente: ${patientName}`}
      >
        <C.CardHeader>
          <C.AvatarCircle aria-hidden="true">{getInitials(patient.name)}</C.AvatarCircle>

          <OverlayTrigger placement="top" overlay={<Tooltip>{patientName}</Tooltip>}>
            <C.EntityName>{patientName}</C.EntityName>
          </OverlayTrigger>

          <Dropdown align="end">
            <Dropdown.Toggle as="div" bsPrefix="no-caret">
              <C.ActionButton aria-label={`Ações para ${patientName}`} aria-haspopup="true">
                <i className="uil uil-ellipsis-v" aria-hidden="true" />
              </C.ActionButton>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as="button" onClick={() => setPatientEditModalShow(true)}>
                <i className="uil uil-edit-alt me-2" aria-hidden="true" />
                Editar
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                onClick={() => navigate("/orcamentos-paciente", { state: patient.id })}
              >
                <i className="uil uil-file-alt me-2" aria-hidden="true" />
                Ver orçamentos
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                as="button"
                className={isActive ? "text-danger" : "text-success"}
                onClick={() => setAlertModalShow(true)}
              >
                <i className={`uil uil-${isActive ? "times" : "check"}-circle me-2`} aria-hidden="true" />
                {isActive ? "Desativar" : "Ativar"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </C.CardHeader>

        <C.CardBody aria-label="Dados do paciente">
          <C.MetaItem>
            <C.MetaLabel>CPF</C.MetaLabel>
            <C.MetaValue>{cpfAndCnpjMask(patient.cpf)}</C.MetaValue>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Telefone</C.MetaLabel>
            <C.MetaValue>{phone || "—"}</C.MetaValue>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>E-mail</C.MetaLabel>
            <OverlayTrigger placement="top" overlay={<Tooltip>{patient.email}</Tooltip>}>
              <C.MetaValue>{patient.email || "—"}</C.MetaValue>
            </OverlayTrigger>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Endereço</C.MetaLabel>
            {address ? (
              <OverlayTrigger placement="top" overlay={<Tooltip>{address}</Tooltip>}>
                <C.MetaValue>{address}</C.MetaValue>
              </OverlayTrigger>
            ) : (
              <C.MetaValueMuted>Não informado</C.MetaValueMuted>
            )}
          </C.MetaItem>
        </C.CardBody>
      </C.Card>

      <AlertModal
        show={alertModalShow}
        onHide={() => setAlertModalShow(false)}
        title={`Tem certeza que deseja ${isActive ? "des" : ""}ativar esse paciente?`}
        onClickButton={handleActivatePatient}
        btnVariant={isActive ? "danger" : "success"}
        isLoading={isLoading}
      />
      <ModalComponent
        title="Editar Paciente"
        show={patientEditModalShow}
        onHide={() => setPatientEditModalShow(false)}
        size="xl"
      >
        <PatientEditForm
          onHide={() => setPatientEditModalShow(false)}
          patient={patient}
          companyId={companyId}
        />
      </ModalComponent>
    </>
  );
};

export default PatientCard;
